import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Switch,
  Tag,
  Typography
} from "antd";
import { PlusOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import SearchBarComp from "../../../commons/SearchBar/SearchBar";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import { createSubcontractor, getMeta, listSubcontractors, runExpiryCheck } from "./api";
import ComplianceTag from "./ComplianceTag";
import { formatDay, typeLabel } from "./format";
import "./subcontractors.css";

const NewSubcontractorModal = ({ open, meta, onClose }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [form] = Form.useForm();
  const [mode, setMode] = useState("new");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ hasEmployees: true, bauleistungen: true });
      setMode("new");
    }
  }, [open]);

  const submit = async values => {
    setBusy(true);
    try {
      const res = await createSubcontractor({
        ...(mode === "existing"
          ? { supplier: values.supplier }
          : {
              newSupplier: {
                name: values.name,
                email: values.email,
                phone: values.phone,
                taxNumber: values.taxNumber
              }
            }),
        trade: values.trade,
        contactPerson: values.contactPerson,
        email: values.email,
        bauleistungen: values.bauleistungen,
        requirements: {
          hasEmployees: values.hasEmployees,
          sokaBau: values.sokaBau,
          foreignWorkers: values.foreignWorkers
        }
      });
      history.push(`/user/subcontractors/${res.data.data._id}`);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="subcontractors-page"
      open={open}
      width={640}
      title={t("New subcontractor")}
      okText={t("Create")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Segmented
        block
        className="mb-3"
        value={mode}
        onChange={setMode}
        options={[
          { value: "new", label: t("New company") },
          {
            value: "existing",
            label: t("From suppliers"),
            disabled: !meta?.suppliers?.length
          }
        ]}
      />
      <Form form={form} layout="vertical" onFinish={submit}>
        <Row gutter={[12, 0]}>
          {mode === "existing" ? (
            <Col xs={24}>
              <Form.Item name="supplier" label={t("Supplier")} rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={(meta?.suppliers || []).map(s => ({
                    value: s._id,
                    label: `${s.name} (${s.supplierNumber})`
                  }))}
                />
              </Form.Item>
            </Col>
          ) : (
            <>
              <Col xs={24}>
                <Form.Item name="name" label={t("Company name")} rules={[{ required: true }]}>
                  <Input maxLength={200} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="phone" label={t("Phone")}>
                  <Input maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="taxNumber" label={t("Tax number")}>
                  <Input maxLength={50} />
                </Form.Item>
              </Col>
            </>
          )}
          <Col xs={24} md={12}>
            <Form.Item name="trade" label={t("Trade")}>
              <Input maxLength={200} placeholder={t("e.g. drywall, tiling")} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="contactPerson" label={t("Contact person")}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item
              name="email"
              label={t("Email for document requests")}
              rules={[{ type: "email" }]}
            >
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="bauleistungen"
              label={t("Performs construction work")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="hasEmployees" label={t("Has employees")} valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="sokaBau"
              label={t("Main construction trade (SOKA-BAU)")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="foreignWorkers"
              label={t("Workers posted from abroad (A1)")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

const Subcontractors = () => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const isMobile = useIsMobile();
  const [meta, setMeta] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [level, setLevel] = useState("all");
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listSubcontractors({
        q: searchText || undefined,
        level: level === "all" ? undefined : level
      });
      setData(res.data.data);
      if (level === "all") setCounts(res.data.counts);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
  }, []);

  useEffect(() => {
    load();
  }, [searchText, level]);

  const checkNow = async () => {
    setChecking(true);
    try {
      const res = await runExpiryCheck();
      const result = res.data.data;
      Success(
        result.reminders
          ? t("{{n}} reminders sent", { n: result.reminders })
          : t("Nothing due today")
      );
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setChecking(false);
    }
  };

  const columns = [
    {
      title: t("Subcontractor"),
      key: "name",
      mobile: "title",
      render: (_, item) => (
        <div>
          <Link to={`/user/subcontractors/${item._id}`}>
            <Typography.Text strong>{item.name}</Typography.Text>
          </Link>
          <div className="sc-small">
            {[item.trade, item.contactPerson].filter(Boolean).join(" · ") || item.supplierNumber}
          </div>
        </div>
      )
    },
    {
      title: t("Documents"),
      key: "level",
      mobile: "badge",
      width: 180,
      render: (_, item) => <ComplianceTag compliance={item.compliance} />
    },
    {
      title: t("Open points"),
      key: "problems",
      mobile: "full",
      render: (_, item) => {
        const c = item.compliance;
        const keys = [...c.problems, ...c.expiring];
        return keys.length ? (
          <Space size={[4, 4]} wrap>
            {keys.slice(0, 3).map(key => (
              <Tag key={key} color={c.expiring.includes(key) ? "warning" : "error"}>
                {typeLabel(meta?.documentTypes, key)}
              </Tag>
            ))}
            {keys.length > 3 && <Tag>+{keys.length - 3}</Tag>}
          </Space>
        ) : (
          <span className="sc-small">
            {c.nextExpiry ? t("Next expiry {{date}}", { date: formatDay(c.nextExpiry) }) : "—"}
          </span>
        );
      }
    },
    {
      title: t("Orders"),
      key: "orders",
      width: 90,
      align: "right",
      render: (_, item) => item.openOrders || "—"
    }
  ];

  const isEmpty = !loading && !data.length && !searchText && level === "all";

  return (
    <div className="subcontractors-page">
      <PageToolbar
        filters={
          <Space direction="vertical" className="sc-full">
            <SearchBarComp handleSearch={setSearchText} />
            <Segmented
              value={level}
              onChange={setLevel}
              options={[
                { value: "all", label: t("All") },
                { value: "red", label: `${t("Missing")} (${counts.red || 0})` },
                { value: "amber", label: `${t("Please check")} (${counts.amber || 0})` },
                { value: "green", label: `${t("Complete")} (${counts.green || 0})` }
              ]}
            />
          </Space>
        }
        actions={
          <Space wrap>
            {meta?.can.runCheck && (
              <Button icon={<SafetyCertificateOutlined />} loading={checking} onClick={checkNow}>
                {t("Check expiry now")}
              </Button>
            )}
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
                {t("New subcontractor")}
              </Button>
            )}
          </Space>
        }
      />

      {isEmpty ? (
        <Card>
          <Empty
            description={
              <>
                <Typography.Title level={5}>
                  {t("Subcontractors with their documents")}
                </Typography.Title>
                <Typography.Paragraph type="secondary" className="sc-empty-text">
                  {t(
                    "Keep Freistellungsbescheinigung, Unbedenklichkeitsbescheinigungen and insurance of your subcontractors in one place, get reminded before they expire and see before every payment whether 15 % must be withheld."
                  )}
                </Typography.Paragraph>
              </>
            }
          >
            {meta?.can.create && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewOpen(true)}>
                {t("New subcontractor")}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        (() => {
          const table = (
            <ResponsiveTable
              rowKey="_id"
              loading={loading}
              columns={columns}
              dataSource={data}
              onRow={record => ({
                onDoubleClick: () => history.push(`/user/subcontractors/${record._id}`)
              })}
              pagination={data.length > 50 ? { pageSize: 50 } : false}
              scroll={{ x: 720 }}
            />
          );
          return isMobile ? table : <Card styles={{ body: { padding: 0 } }}>{table}</Card>;
        })()
      )}

      <NewSubcontractorModal open={newOpen} meta={meta} onClose={() => setNewOpen(false)} />
    </div>
  );
};

export default Subcontractors;
